import { useFormik } from 'formik';
// mantine
import {
    Autocomplete,
    Button,
    Group,
    Switch,
    TextInput,
    UnstyledButton
} from '@mantine/core';
// components
import { processFormData } from '@newo/toolkit/bff';
import type { TAttribute, TAttributeData } from '@newo/api/gateway';
import AttributeValue from '@/pages/Attributes/components/AttributeValue';
import MDEditor from '@/components/MDEditor';
// utils
import validationSchema from '@/pages/Attributes/components/AttributeForm/AttributeForm.schema.ts';

interface PersonaFormProps {
    onClose?: () => void;
    attribute?: TAttribute;
    groups: string[];
    onSubmit: (data: {
        attributeId?: string;
        data: TAttributeData;
    }) => Promise<unknown>;
}

const AttributeForm = ({
    attribute,
    groups,
    onClose,
    onSubmit
}: PersonaFormProps) => {
    const initialValues: TAttributeData = {
        idn: attribute?.idn ?? '',
        value: attribute?.value ?? '',
        title: attribute?.title ?? '',
        description: attribute?.description ?? '',
        group: attribute?.group ?? '',
        is_hidden: attribute?.is_hidden ?? false,
        is_read_only: attribute?.is_read_only ?? false,
        possible_values: attribute?.possible_values ?? [],
        value_type: attribute?.value_type ?? 'string'
    };
    const formik = useFormik({
        initialValues,
        validationSchema,
        async onSubmit(formData) {
            const data = processFormData(formData);

            if (attribute) {
                return await onSubmit({
                    attributeId: attribute.id,
                    data: {
                        ...data,
                        value: data.value
                    }
                });
            }
            return onSubmit({
                data: {
                    ...data,
                    value: data.value
                }
            });
        }
    });

    const handleCancel = () => {
        onClose?.();
    };

    const handleGroupChange = (value: string) => {
        formik.setFieldValue('group', value);
    };

    const handleIsHiddenChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        formik.setFieldValue('is_hidden', event.currentTarget.checked);
    };

    const handleIsReadOnlyChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        formik.setFieldValue('is_read_only', event.currentTarget.checked);
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <TextInput
                withAsterisk
                description="Attribute unique identifier"
                disabled={!!attribute}
                error={
                    formik.touched.idn && formik.values.idn
                        ? formik.errors.idn
                        : undefined
                }
                label="Idn"
                name="idn"
                placeholder="Idn"
                value={formik.values.idn}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <Group>
                <Switch
                    checked={formik.values.is_hidden}
                    label="Is Hidden"
                    size="sm"
                    onChange={handleIsHiddenChange}
                />

                <Switch
                    checked={formik.values.is_read_only}
                    label="Is Read only"
                    size="sm"
                    onChange={handleIsReadOnlyChange}
                />
            </Group>

            <AttributeValue formik={formik} />

            <Autocomplete
                data={groups}
                error={
                    formik.touched.group && formik.values.group
                        ? formik.errors.group
                        : undefined
                }
                label="Group"
                name="group"
                placeholder="Group"
                value={formik.values.group ?? ''}
                onBlur={formik.handleBlur}
                onChange={handleGroupChange}
            />

            <TextInput
                error={
                    formik.touched.title && formik.values.title
                        ? formik.errors.title
                        : undefined
                }
                label="Title"
                name="title"
                placeholder="Title"
                value={formik.values.title ?? ''}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <div>
                <div className="text-xs font-semibold my-1">Description</div>
                <MDEditor
                    value={formik.values.description ?? ''}
                    onChange={(value) => {
                        formik.setFieldValue('description', value);
                    }}
                />
            </div>

            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    {attribute ? 'Save' : 'Create'}
                </Button>
            </div>
        </form>
    );
};

export default AttributeForm;
